import React, { useState } from "react";
import { Play, ExternalLink } from "lucide-react";
import { fileUrl } from "../lib/api";

// Detect platform and build embeddable URL
export function parseEmbed(url = "") {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");

    // YouTube
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = u.searchParams.get("v");
      if (id) return { platform: "youtube", embed: `https://www.youtube.com/embed/${id}`, original: url };
      // Shorts
      const m = u.pathname.match(/^\/shorts\/([^/]+)/);
      if (m) return { platform: "youtube", embed: `https://www.youtube.com/embed/${m[1]}`, original: url };
    }
    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "");
      if (id) return { platform: "youtube", embed: `https://www.youtube.com/embed/${id}`, original: url };
    }

    // Vimeo
    if (host === "vimeo.com") {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return { platform: "vimeo", embed: `https://player.vimeo.com/video/${id}`, original: url };
    }
    if (host === "player.vimeo.com") {
      return { platform: "vimeo", embed: url, original: url };
    }

    // TikTok — use their embed
    if (host.endsWith("tiktok.com")) {
      // /@user/video/<id>
      const m = u.pathname.match(/\/video\/(\d+)/);
      if (m) return { platform: "tiktok", embed: `https://www.tiktok.com/embed/v2/${m[1]}`, original: url };
    }
  } catch (e) {
    // fallthrough
  }
  return { platform: "unknown", embed: url, original: url };
}

const aspectClass = (platform) =>
  platform === "tiktok" ? "aspect-[9/16] max-w-[360px] mx-auto" : "aspect-video";

export default function VideoPlayer({ video, className = "" }) {
  const [playing, setPlaying] = useState(false);

  if (!video) return null;
  const isUpload = video.kind === "upload" || (!video.kind && video.url && !/^https?:\/\//i.test(video.url));

  if (isUpload) {
    const src = fileUrl(video.url);
    return (
      <div className={`relative overflow-hidden bg-[#1A1714] rounded-sm ${aspectClass("upload")} ${className}`}>
        {!playing ? (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 flex items-center justify-center group bg-[#1A1714]"
          >
            {video.poster && (
              <img src={fileUrl(video.poster)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            )}
            <span className="relative z-10 inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F6F1E8]/95 text-[#1A1714] shadow-lg group-hover:scale-105 transition-transform">
              <Play size={22} fill="currentColor" />
            </span>
            {video.title && (
              <span className="absolute bottom-4 left-4 right-4 text-[#F6F1E8] text-sm font-serif-display">
                {video.title}
              </span>
            )}
          </button>
        ) : (
          <video src={src} controls autoPlay className="w-full h-full object-contain bg-black" />
        )}
      </div>
    );
  }

  // Embed
  const { platform, embed, original } = parseEmbed(video.url);
  const cls = aspectClass(platform);

  if (!playing) {
    return (
      <div className={`relative overflow-hidden bg-[#1A1714] rounded-sm ${cls} ${className}`}>
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="absolute inset-0 flex items-center justify-center group"
        >
          <span className="absolute inset-0 bg-gradient-to-br from-[#1A1714] via-[#2c2620] to-[#1A1714]" />
          <span className="relative z-10 flex flex-col items-center gap-3 text-[#F6F1E8]">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F6F1E8]/95 text-[#1A1714] shadow-lg group-hover:scale-105 transition-transform">
              <Play size={22} fill="currentColor" />
            </span>
            <span className="text-[11px] tracking-[0.22em] uppercase opacity-80">
              Play {platform !== "unknown" ? platform : "video"}
            </span>
          </span>
        </button>
        {video.title && (
          <span className="absolute bottom-4 left-4 right-4 text-[#F6F1E8] text-sm font-serif-display z-10 pointer-events-none">
            {video.title}
          </span>
        )}
      </div>
    );
  }

  if (platform === "unknown") {
    return (
      <a
        href={original}
        target="_blank"
        rel="noreferrer"
        className={`relative flex items-center justify-center bg-[#1A1714] text-[#F6F1E8] rounded-sm ${cls} ${className}`}
      >
        <span className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase">
          Open external link <ExternalLink size={14} />
        </span>
      </a>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-sm ${cls} ${className}`}>
      <iframe
        src={`${embed}${embed.includes("?") ? "&" : "?"}autoplay=1`}
        title={video.title || "video"}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
}
